import { ChangeDetectorRef, Component, Input, OnInit, OnDestroy, inject } from '@angular/core';
import { FormBuilder } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { GeneralDataService } from '@app/services/general/general-data.service';
import { OrquestadorService } from '@app/services/orquestador/orquestador.service';
import { Store } from '@ngrx/store';
import { Observable, Subscription } from 'rxjs';
import { selectDataByKey } from 'src/storage/general.selectors';
import { succesngonitplanes } from '@app/interceptors/cotizador/planes/onginit';
import { setData } from 'src/storage/general.actions';
import { environment } from '@env/environment';
import { LoaderService } from '@app/services/general/loader.service';
import { DataLayerService } from '@app/services/dataLeyer/data-leyer.service';
import { ERROR_500_REDIRECT } from '@app/shared/final-pages/final-pages.constants';

/**
 * Componente para visualización y selección de planes de seguro
 * Permite al usuario elegir entre planes predefinidos (Esencial, Plus) o armar un plan personalizado
 */
@Component({
  selector: 'app-planes-seguros',
  templateUrl: './new-planes-seguros.component.html',
  styleUrls: ['./new-planes-seguros.component.scss']
})
export class NewPlanesSegurosComponent implements OnInit, OnDestroy {
  // Servicios inyectados con inject()
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly fb = inject(FormBuilder);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly generalDataService = inject(GeneralDataService);
  private readonly orquestadorService = inject(OrquestadorService);
  private readonly store = inject(Store);
  private readonly loaderService = inject(LoaderService);
  private readonly dataLayerService = inject(DataLayerService);

  // Propiedades del componente
  body: any;
  planesData: any[] = [];
  planesToShow: any[] = []; // Array que contendrá solo los planes Esencial y Plus
  planEsencial: any = null;
  planPlus: any = null;
  planArmaTuPlan: any = null;
  asesorCode = '';
  bitacoraId = '';
  tenant$: Observable<any>;
  leadId$: Observable<any>;
  bitacoraId$: Observable<any>;
  propiedad$: Observable<any>;
  generatedBody: any;
  isClicked = false;
  isLoading = false;
  loaded = false;
  sessionId = '';
  enableDetailsSection: boolean = false;

  private subscriptions = new Subscription();

  constructor() {
    this.tenant$ = this.store.select(selectDataByKey('Tenant'));
    this.leadId$ = this.store.select(selectDataByKey('leadId'));
    this.bitacoraId$ = this.store.select(selectDataByKey('bitacoraId'));
    this.propiedad$ = this.store.select(selectDataByKey('Property'));
  }

  ngOnInit(): void {
    console.log('🔄 Planes Component - Iniciando carga de planes...');

    this.sessionId = this.getLocalStorage().toString();

    // Configurar loader
    this.setupLoader();

    // Cargar planes desde el orquestador
    this.loadPlanes();

    // Suscribirse al código de asesor
    this.subscriptions.add(
      this.store.select(selectDataByKey('codigoAsesor')).subscribe(code => {
        if (code) {
          console.log('Código de asesor desde el store:', code);
          this.asesorCode = code;
        }
      })
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  /**
   * Configura el loader del componente
   */
  private setupLoader(): void {
    this.loaderService.show();
    this.subscriptions.add(
      this.loaderService.loading$.subscribe(({ loading, loaded }) => {
        this.isLoading = loading;
        this.loaded = loaded;
      })
    );
  }

  /**
   * Carga los planes desde el orquestador
   */
  private loadPlanes(): void {
    const options: any = {
      keys: ['Property'],
      leadId: 'leadId',
      asesorCode: this.asesorCode !== '' ? this.asesorCode : undefined,
      bitacoraId: this.bitacoraId !== '' ? this.bitacoraId : undefined
    };

    console.log('📊 Obteniendo datos del store para planes...');

    this.generalDataService.getbody(options).subscribe(body => {
      // ✅ Clonar el body para no modificar el store
      this.body = JSON.parse(JSON.stringify(body));
      this.body.getPriceData = true;

      // ✅ Agregar campos quemados solo para la petición de planes
      this.body.productsConfigurationData.Property = {
        ...this.body.productsConfigurationData.Property,
        CityProperty: "Villavicencio",
        Department: "Meta"
      };

      console.log('Generated body para obtener planes (ngOnInit):', this.body);
      console.log('📋 leadId en body:', this.body.leadId ? `✅ ${this.body.leadId}` : '⚠️ No disponible');

      // Llamar al orquestador solo después de tener el body completo
      this.fetchPlansFromOrquestador();
    });
  }

  /**
   * Obtiene los planes del orquestador
   */
  private fetchPlansFromOrquestador(): void {
    console.log('✅ Llamando a orquestador para obtener planes...');

    this.orquestadorService.postOrquestador(this.body).subscribe({
      next: (data) => this.handlePlansResponse(data),
      error: (error) => this.handlePlansError(error)
    });
  }

  /**
   * Maneja la respuesta exitosa del orquestador con los planes
   */
  private handlePlansResponse(data: any): void {
    console.log('📦 Respuesta del orquestador (planes):', data);

    // Procesar la respuesta para extraer los planes
    if (data && data.data && Array.isArray(data.data)) {
      this.planesData = data.data;
    } else {
      // Si no hay datos del servicio, redirigir a cotización-pausa
      console.warn('⚠️ No se recibieron planes del orquestador, redirigiendo a cotización-pausa');
      this.router.navigate(['seguros/hogar/arriendo/cotizador/cotizacion-pausa']);
      return;
    }

    this.processPlansData();
    this.loaderService.hide();
  }

  /**
   * Maneja los errores al obtener planes del orquestador
   */
  private handlePlansError(error: any): void {
    console.error('❌ Error al obtener datos del servicio:', error);

    console.error('🚨 Error 500 detectado - Redirigiendo a página de error...');
    this.loaderService.hide();
    this.router.navigate([ERROR_500_REDIRECT]);
    return;


    // 
    // console.warn('⚠️ Usando datos del mock debido a error diferente de 500');
    // this.planesData = succesngonitplanes.data;

    // this.processPlansData();

    // console.log('✅ Planes cargados desde mock:', {
    //   total: this.planesData.length,
    //   planesToShow: this.planesToShow.length,
    //   esencial: !!this.planEsencial,
    //   plus: !!this.planPlus,
    //   armaTuPlan: !!this.planArmaTuPlan
    // });

    // this.loaderService.hide();
  }

  /**
   * Procesa los datos de planes recibidos
   * Filtra y asigna los planes Esencial, Plus y Arma tu plan
   */
  private processPlansData(): void {
    // Filtrar solo los planes Esencial y Plus para mostrar
    this.planesToShow = this.planesData.filter(plan =>
      plan.planName === 'Esencial' || plan.planName === 'Plus'
    );

    // Mantener referencias individuales por compatibilidad
    this.planEsencial = this.planesData.find(plan =>
      plan.planName === 'Esencial' || plan.productCode === 'ARRENDAMIENTO_PlanEsencial'
    );

    this.planPlus = this.planesData.find(plan =>
      plan.planName === 'Plus' || plan.productCode === 'ARRENDAMIENTO_PlanPlus'
    );

    this.planArmaTuPlan = this.planesData.find(plan =>
      plan.planName === 'Arma tu plan' || plan.productCode === 'ARRENDAMIENTO_ArmaTuPlan'
    );

    this.planesData.forEach(plan => {
      plan.showDetails = false;
      if (plan.coverages) {

        if (plan.planName === 'Arma tu plan' || plan.productCode === 'ARRENDAMIENTO_ArmaTuPlan') {
          const totalOpcionales = plan.coverages
            .filter((c: any) => c.isOptional === true)
            .reduce((sum: number, c: any) => sum + (c.price || 0), 0);

          plan.price = (plan.price || 0) - totalOpcionales;
          plan.coverages.forEach((c: any) => {
            if (c.isOptional) {
              c.selected = false;
            }
          });
        }
      }
    });

    console.log('✅ Detalles de meses ajustados y administración inyectada.');


    const pageviewAttributes: Record<string, string> = {
      'Arma tu plan': String(this.planArmaTuPlan?.price ?? ''),
      'Plan Esencial': String(this.planEsencial?.price ?? ''),
      'Plan Plus': String(this.planPlus?.price ?? '')
    };

    this.dataLayerService.logDataNewFlow(
      'Arriendo_ecommerce_step_1_Planes',
      'Arriendo',
      'Planes',
      'Pageview',
      this.sessionId,
      pageviewAttributes
    );
  }

  /**
   * Navega a la pantalla de armar plan personalizado
   */
  ArmarPlan(nameplan: string, plan: any): void {
    const buttonAttributes: Record<string, string> = {
      'Arma tu plan': String(this.planArmaTuPlan?.price ?? ''),
      'Plan Esencial': String(this.planEsencial?.price ?? ''),
      'Plan Plus': String(this.planPlus?.price ?? '')
    };

    this.dataLayerService.logPlanesNewFlow(
      'Arriendo_ecommerce_step_1_Planes',
      'Arriendo',
      'Planes',
      'Button',
      this.sessionId,
      `Plan ${nameplan}`,
      String(plan?.price ?? ''),
      true,
      buttonAttributes
    );
    this.savePlanToStore(nameplan, plan);
    this.router.navigate(['seguros/hogar/arriendo/cotizador/armar-plan']);
  }

  /**
   * Maneja el submit de selección de un plan
   */
  onSubmit(nameplan: string, plan: any): void {
    this.isClicked = true;

    const hasPublicServices = plan.coverages?.find((c: any) =>
      c.name.toLowerCase().includes('servicios públicos') ||
      c.name.toLowerCase().includes('public services') ||
      c.id === 2 || c.id === '2'
    )?.selected;

    const hasAssistance = plan.coverages?.find((c: any) =>
      c.name.toLowerCase().includes('asistencia') ||
      c.id === 3 || c.id === '3'
    )?.selected;

    const optionalProducts = {
      PRD_CoveragePublicServices: hasPublicServices === true ? "true" : "false",
      PRD_CoverageAsistence: hasAssistance === true ? "true" : "false",
    };

    this.store.dispatch(setData({
      key: 'optionalProducts',
      value: optionalProducts,
      merge: false
    }));

    if (nameplan === 'Arma tu plan') {
      plan.price = this.getTotal();
    }

    const priceToLog = plan?.price ?? '';

    const buttonAttributes: Record<string, string> = {
      'Arma tu plan': String(this.planArmaTuPlan?.price ?? ''),
      'Plan Esencial': String(this.planEsencial?.price ?? ''),
      'Plan Plus': String(this.planPlus?.price ?? '')
    };

    this.dataLayerService.logPlanesNewFlow(
      'Arriendo_ecommerce_step_1_Planes',
      'Arriendo',
      'Planes',
      'Button',
      this.sessionId,
      `Plan ${plan?.planName}`,
      String(priceToLog),
      true,
      buttonAttributes
    );

    this.savePlanToStore(nameplan, plan);

    console.log('Formulario enviado');

    const options = {
      keys: ['Tenant', 'Property', 'ProductCode'],
      screenPercentage: '25',
      leadId: 'leadId',
    };

    window.scrollTo(0, 0);
    this.loaderService.show();

    this.generalDataService.getbody(options).subscribe(body => {
      this.generatedBody = body;
      this.generatedBody = { ...body, optionalProducts };
      console.log('Generated body (planes-seguros):', this.generatedBody);
      console.log('📋 leadId en body:', this.generatedBody.leadId ? `✅ ${this.generatedBody.leadId}` : '⚠️ No disponible');
      this.asesorCode = body.asesorCode;
      this.bitacoraId = body.bitacoraId;

      this.submitToOrquestador();
    });
  }

  /**
   * Guarda el plan seleccionado en el store
   */
  private savePlanToStore(nameplan: string, plan: any): void {

    const planConDatosExtra = {
      ...plan,
      armaTuPlan: String(this.planArmaTuPlan?.price ?? ''),
      planEsencial: String(this.planEsencial?.price ?? ''),
      planPlus: String(this.planPlus?.price ?? '')
    };
    this.store.dispatch(setData({
      key: 'ProductCode',
      value: nameplan,
      merge: false
    }));

    this.store.dispatch(setData({
      key: 'Plan',
      value: planConDatosExtra,
      merge: false
    }));
  }

  /**
   * Envía los datos al orquestador después de seleccionar un plan
   */
  private submitToOrquestador(): void {
    this.orquestadorService.postOrquestador(this.generatedBody).subscribe({
      next: (data) => this.handleSubmitResponse(data)
    });
  }

  /**
   * Maneja la respuesta del orquestador después del submit
   */
  private handleSubmitResponse(data: any): void {
    console.log('Response from postOrquestador: envio generacion', data);
    this.loaderService.hide();
    this.router.navigate(['seguros/hogar/arriendo/cotizador/datos-propietario']);
  }

  /**
   * Obtiene el session_id del localStorage
   */
  private getLocalStorage(): number {
    return Number(localStorage.getItem('session_id'));
  }

  /**
   * Verifica si hay coberturas excluidas en el plan Esencial
   */
  hasExcludedCoverages(): boolean {
    return this.planEsencial?.coverages?.some((coverage: any) =>
      coverage.isOptional && coverage.name === 'Paquete de asistencias básicas'
    ) || false;
  }

  /**
   * Verifica si hay coberturas diferenciales en el plan Plus
   */
  hasDifferentialCoverages(): boolean {
    return this.planPlus?.coverages?.some((coverage: any) =>
      coverage.isOptional && coverage.name === 'Paquete de asistencias básicas'
    ) || false;
  }

  /**
   * Obtiene las coberturas que están en Plus pero no en Esencial
   */
  getExcludedCoveragesFromEsencial(): any[] {
    if (!this.planEsencial || !this.planPlus) {
      return [];
    }

    // Obtener los nombres de las coberturas del plan Esencial
    const esencialCoverageNames = this.planEsencial.coverages.map((coverage: any) => coverage.name);

    // Filtrar las coberturas del plan Plus que no están en Esencial
    const excludedCoverages = this.planPlus.coverages.filter((coverage: any) =>
      !esencialCoverageNames.includes(coverage.name)
    );

    return excludedCoverages;
  }

  /**
   * Obtiene las coberturas obligatorias de Arma tu plan
   */
  getRequiredCoveragesFromArmaTuPlan(): any[] {
    if (!this.planArmaTuPlan) {
      return [];
    }
    return this.planArmaTuPlan.coverages.filter((coverage: any) => !coverage.isOptional);
  }

  /**
   * Obtiene las coberturas opcionales de Arma tu plan
   */
  getOptionalCoveragesFromArmaTuPlan(): any[] {
    if (!this.planArmaTuPlan) {
      return [];
    }
    return this.planArmaTuPlan.coverages.filter((coverage: any) => coverage.isOptional);
  }

  /**
   * Obtiene las coberturas excluidas de un plan específico
   */
  getExcludedCoveragesFromPlan(currentPlan: any): any[] {
    if (!this.planEsencial || !this.planPlus || !currentPlan) {
      return [];
    }

    // Si es el plan Esencial, devolver las coberturas que están en Plus pero no en Esencial
    if (currentPlan.planName === 'Esencial') {
      const esencialCoverageNames = this.planEsencial.coverages.map((coverage: any) => coverage.name);
      return this.planPlus.coverages.filter((coverage: any) =>
        !esencialCoverageNames.includes(coverage.name)
      );
    }

    // Si es el plan Plus, no tiene coberturas excluidas
    if (currentPlan.planName === 'Plus') {
      return [];
    }

    return [];
  }

  validationEnableDetailSetion(plan: any) {
    plan.showDetails = !plan.showDetails;
  }

  /**
   * Calcula el precio total de Arma tu plan incluyendo coberturas opcionales seleccionadas
   */
  getTotal(): number {
    if (!this.planArmaTuPlan) return 0;
    const basePrice = this.planArmaTuPlan.price || 0;
    const optionalPrice = this.planArmaTuPlan.coverages
      .filter((c: any) => c.selected && c.price)
      .reduce((sum: number, c: any) => sum + c.price, 0);
    return basePrice + optionalPrice;
  }

  /**
   * Maneja el cambio en la selección de coberturas opcionales
   */
  onCoverageChange() {
    this.cdr.detectChanges();
  }

  public getRequiredCoveragesFromPlan(plan: any): any[] {
    return plan?.coverages?.filter((coverage: any) => !coverage.isOptional) ?? [];
  }

  public getOptionalCoveragesFromPlan(plan: any): any[] {
    return plan?.coverages?.filter((coverage: any) => coverage.isOptional) ?? [];
  }
}
